import React, {useContext, useMemo} from 'react';
import {
    WidgetLockController,
    WidgetRenderingContextType
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {assertNotNullable, ObservableValue} from '@wix/devzai-utils-common';
import {reactProvideContextValues} from '@wix/devzai-utils-react';

type WidgetRenderingContextData = {
    widgetRenderingContextType: WidgetRenderingContextType,
    lockController?: WidgetLockController;
    isFullScreen?: boolean;
    hideHeader?: boolean;
    renderingInnerStateController: ObservableValue<boolean>;
    observableCurrentItemIdToRender?: ObservableValue<string | null>;
    headerCustomSettings?: {
        title: string;
        subtitle?: string;
        subtitleLines?: 2 | 3 | 4 | 8;
        backButtonCallback?: () => void;
        dismissButtonCallback?: () => void;
    },
    closeCallback?: () => void;
}

const WidgetRenderingContext = React.createContext<WidgetRenderingContextData | null>(null);

export function useWidgetRenderingContext() {
    return useContext(WidgetRenderingContext);
}

export function useAssertWidgetRenderingContext() {
    return assertNotNullable(useContext(WidgetRenderingContext));
}

const WidgetRenderingContextTypeReactContext = React.createContext<WidgetRenderingContextType | null>(null);

export function useAssertWidgetRenderingContextType () {
    return assertNotNullable(useContext(WidgetRenderingContextTypeReactContext), `WidgetRenderingContextTypeReactContext is not provided`);
}

export const DoppeViewerWidgetRenderingScope = React.memo(function DoppeViewerWidgetRenderingScope (
    props: WidgetRenderingContextData & {
        children: React.ReactNode;
    }
) {
    const {
        children,

        widgetRenderingContextType,
        lockController,
        isFullScreen,
        hideHeader,
        renderingInnerStateController,
        observableCurrentItemIdToRender,
        headerCustomSettings,
        closeCallback,
    } = props;

    const widgetRenderingContextData = useMemo(() => {
        return {
            widgetRenderingContextType,
            lockController,
            isFullScreen,
            hideHeader,
            renderingInnerStateController,
            observableCurrentItemIdToRender,
            headerCustomSettings,
            closeCallback,
        } satisfies Record<keyof WidgetRenderingContextData, any>
    }, [widgetRenderingContextType, lockController, isFullScreen, hideHeader, renderingInnerStateController, observableCurrentItemIdToRender, headerCustomSettings, closeCallback]);

    return reactProvideContextValues()
        .addContextValue(WidgetRenderingContext, widgetRenderingContextData)
        .addContextValue(WidgetRenderingContextTypeReactContext, widgetRenderingContextType)
        .render(
            <>
                {children}
            </>
        );
});

export function useIsWidgetInFixedHeight() {
    const widgetRenderingContextType = useContext(WidgetRenderingContextTypeReactContext);

    if (widgetRenderingContextType === null) {
        return false;
    }

    return widgetRenderingContextType !== WidgetRenderingContextType.Card && widgetRenderingContextType !== WidgetRenderingContextType.Strip;
}